import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeIn } from "@/hooks/use-scroll-animation";
import { supabase } from "@/integrations/supabase/client";

const ContactForm = () => {
  const { toast } = useToast();
  const lang = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [honeypot, setHoneypot] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const t = lang === "en"
    ? {
        heading: "Get in touch",
        subheading: "Request a free estimate or ask about our services.",
        name: "Name *",
        email: "Email *",
        phone: "Phone number",
        message: "Message *",
        submit: "Send message",
        submitting: "Sending...",
        required: "Please fill in all required fields",
        maxLength: "Maximum field length exceeded",
        success: "Thank you for your message!",
        successDesc: "We'll get back to you as soon as possible.",
        error: "Something went wrong. Please try again.",
      }
    : {
        heading: "Ota yhteyttä",
        subheading: "Pyydä ilmainen arvio tai kysy lisätietoja palveluistamme.",
        name: "Nimi *",
        email: "Sähköposti *",
        phone: "Puhelinnumero",
        message: "Viesti *",
        submit: "Lähetä viesti",
        submitting: "Lähetetään...",
        required: "Täytä kaikki pakolliset kentät",
        maxLength: "Kentän maksimipituus ylitetty",
        success: "Kiitos yhteydenotostasi!",
        successDesc: "Palaamme asiaan mahdollisimman pian.",
        error: "Jokin meni pieleen. Yritä uudelleen.",
      };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Honeypot check — bots fill hidden fields
    if (honeypot) return;

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast({ title: t.required, variant: "destructive" });
      return;
    }
    if (form.name.length > 100 || form.email.length > 255 || form.message.length > 2000) {
      toast({ title: t.maxLength, variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          message: form.message.trim(),
        },
      });

      if (error) throw error;

      toast({ title: t.success, description: t.successDesc });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error('Contact form error:', err);
      toast({ title: t.error, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id={lang === "en" ? "contact" : "yhteystiedot"} className="py-20 bg-secondary">
      <div className="container max-w-2xl">
        <FadeIn>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-center mb-3">{t.heading}</h2>
          <p className="text-center text-muted-foreground mb-10">{t.subheading}</p>
        </FadeIn>

        <FadeIn delay={150}>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Honeypot field - hidden from real users, bots will fill it */}
            <div aria-hidden="true" className="absolute opacity-0 -z-10 h-0 w-0 overflow-hidden">
              <label htmlFor="contact-website">Website</label>
              <input id="contact-website" type="text" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium mb-1.5">{t.name}</label>
                <input id="contact-name" type="text" name="name" autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-md border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm" maxLength={100} required disabled={isSubmitting} />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium mb-1.5">{t.email}</label>
                <input id="contact-email" type="email" name="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-md border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm" maxLength={255} required disabled={isSubmitting} />
              </div>
            </div>
            <div>
              <label htmlFor="contact-phone" className="block text-sm font-medium mb-1.5">{t.phone}</label>
              <input id="contact-phone" type="tel" name="phone" autoComplete="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 rounded-md border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm" maxLength={20} disabled={isSubmitting} />
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-sm font-medium mb-1.5">{t.message}</label>
              <textarea id="contact-message" name="message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="w-full px-4 py-3 rounded-md border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm resize-none" maxLength={2000} required disabled={isSubmitting} />
            </div>
            <button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground py-3 rounded-md font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
              {isSubmitting ? t.submitting : t.submit}
            </button>
          </form>
        </FadeIn>
      </div>
    </section>
  );
};

export default ContactForm;
